import { FC } from "react";
import { Button } from "react-bootstrap";
import { Budget } from "@models/budget.model";
import { formatCurrency } from "src/shared/utils/formatCurrency";
import {
  BUDGET_TYPE_LABELS,
  REPLENISH_FREQUENCY_LABELS,
} from "../../types";

interface Props {
  budgets: readonly Budget[];
  /** When provided, shows a "+ Add" button. Omit for read-only. */
  onCreate?: () => void;
  onEdit?: (budget: Budget) => void;
  onDelete?: (budget: Budget) => void;
}

const ContractBudgetsPanel: FC<Props> = ({ budgets, onCreate, onEdit, onDelete }) => {
  const editable = !!(onEdit || onDelete || onCreate);

  return (
    <div>
      {editable && (
        <div className="contract-section-header">
          <span className="section-hint">Funding rules for this contract</span>
          {onCreate && (
            <Button variant="primary" size="sm" onClick={onCreate}>
              <i className="bi bi-plus-circle me-1" />
              Add Budget
            </Button>
          )}
        </div>
      )}

      {budgets.length === 0 ? (
        <div className="text-center py-4 text-muted">
          <i className="bi bi-cash-stack fs-3 d-block mb-2 opacity-50" />
          <div className="small">No budgets configured</div>
        </div>
      ) : (
        budgets.map((budget) => (
          <BudgetCard
            key={budget.id}
            budget={budget}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))
      )}
    </div>
  );
};

interface CardProps {
  budget: Budget;
  onEdit?: (budget: Budget) => void;
  onDelete?: (budget: Budget) => void;
}

function BudgetCard({ budget, onEdit, onDelete }: CardProps) {
  const isSubscription = budget.budget_type === "SUBSCRIPTION";
  const period = `${budget.start_date}${budget.end_date ? ` — ${budget.end_date}` : " — Ongoing"}`;

  return (
    <div className="budget-card">
      <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
        <div className="flex-grow-1">
          <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
            <span className="budget-card__period">{period}</span>
            <span className="contract-status-badge type">
              {BUDGET_TYPE_LABELS[budget.budget_type] ?? budget.budget_type}
            </span>
            <span className={`contract-status-badge ${budget.is_active ? "active" : "inactive"}`}>
              {budget.is_active ? "Active" : "Inactive"}
            </span>
          </div>
        </div>
        {(onEdit || onDelete) && (
          <div className="d-flex gap-1">
            {onEdit && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => onEdit(budget)}
                title="Edit"
              >
                <i className="bi bi-pencil" />
              </Button>
            )}
            {onDelete && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => onDelete(budget)}
                title="Delete"
              >
                <i className="bi bi-trash" />
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="d-flex gap-4 flex-wrap">
        <div>
          <div className="stat-label">Budget amount</div>
          <div className="stat-value">{formatCurrency(budget.starting_balance)}</div>
        </div>
        {isSubscription && budget.replenish_amount != null && (
          <div>
            <div className="stat-label">Replenish</div>
            <div className="stat-value accent">
              {formatCurrency(budget.replenish_amount)}
              {budget.replenish_frequency && (
                <span
                  className="ms-1 fw-normal"
                  style={{ color: "#64748b", fontSize: "0.78rem" }}
                >
                  / {REPLENISH_FREQUENCY_LABELS[budget.replenish_frequency] ?? budget.replenish_frequency}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ContractBudgetsPanel;
